/**
 * Expanding the entity graph one hop at a time (sites#249 follow-up): which
 * nodes can be expanded, what a fetched neighbourhood adds to the drawing and
 * where the new nodes go. Pure functions, shared by the loader
 * (entity-graph.ts) and the renderer (entity-graph-view.ts).
 */
import type { EntityGraph, GraphEdge, GraphNode, GraphTarget } from './entity-graph-types';

/** The drawing stops growing here: past a few hundred nodes it is no longer readable. */
export const MAX_NODES = 400;

/** Labels nodeGraph draws by graph id (api.elect.info entity-graph/node-graph.ts NODE_GRAPH_LABELS). */
const NODE_GRAPH_LABELS = new Set(['Individual', 'State', 'District', 'County', 'Place', 'PostalCode', 'Tract', 'Block', 'Precinct']);

/**
 * The graph to fetch when a reader expands a node in the drawing: a committee
 * or candidate by FEC id (entityGraph), an Individual or geography by graph id
 * (nodeGraph); null for the rest (party, office, cycle, vendor, employer,
 * election stages), which have no neighbourhood query of their own.
 */
export function expandTarget(n: GraphNode): GraphTarget | null {
  if (n.focal) return null;
  if ((n.label === 'Committee' || n.label === 'Candidate') && n.fecId) return { kind: 'fec', fecId: n.fecId, title: n.displayName };
  if (NODE_GRAPH_LABELS.has(n.label)) return { kind: 'node', label: n.label, id: n.id, title: n.displayName };
  return null;
}

/**
 * Positions for a node's newly added neighbours: an arc on the far side of
 * the node from the centre (the focal node), wider and in more rings as the
 * count grows, so expansions spread outward instead of over the first ring.
 */
export function expansionPositions(
  origin: { x: number; y: number },
  ids: string[],
): Map<string, { x: number; y: number }> {
  const pos = new Map<string, { x: number; y: number }>();
  if (!ids.length) return pos;
  const away = origin.x === 0 && origin.y === 0 ? -Math.PI / 2 : Math.atan2(origin.y, origin.x);
  const perRing = 12;
  ids.forEach((id, i) => {
    const ring = Math.floor(i / perRing);
    const inRing = Math.min(perRing, ids.length - ring * perRing);
    const spread = Math.min(Math.PI * 1.1, 0.3 * Math.max(1, inRing));
    const angle = away - spread / 2 + (inRing === 1 ? spread / 2 : (spread * (i % perRing)) / (inRing - 1));
    const r = 150 + ring * 80;
    pos.set(id, { x: origin.x + r * Math.cos(angle), y: origin.y + r * Math.sin(angle) });
  });
  return pos;
}

/** The part of a fetched neighbourhood that is new to the drawing, within the node budget. */
export function newElements(
  graph: EntityGraph,
  hasNode: (id: string) => boolean,
  hasEdge: (id: string) => boolean,
  budget: number,
): { nodes: GraphNode[]; edges: GraphEdge[]; dropped: number } {
  const fresh = graph.nodes.filter((n) => !hasNode(n.id));
  const nodes = fresh.slice(0, Math.max(0, budget));
  const kept = new Set(nodes.map((n) => n.id));
  const edges = graph.edges.filter(
    (e) => !hasEdge(e.id) && (hasNode(e.source) || kept.has(e.source)) && (hasNode(e.target) || kept.has(e.target)),
  );
  return { nodes: nodes.map((n) => ({ ...n, focal: false })), edges, dropped: fresh.length - nodes.length };
}
